import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetAllCustomersQuery, useGetCustomerManagersQuery } from '../../../redux/api/customersApi'
import { Dialog } from '../../common/Dialog/Dialog'
import './SelectAndEditModal.scss'
import {
  isSupplierOrder,
  useUpdateOrderInternalMutation,
  useUpdateOrderSuppliersMutation,
} from '../../../redux/api/ordersApi'
import { TransparentButton, ButtonType } from '../../common/Buttons/TransparentButton'
import { ContactKind } from '@ail/customers-api'
import {
  OrderInternalFormFieldViewModel,
  OrderInternalInputModel,
  OrderInternalViewModel,
  OrderSuppliersFormFieldViewModel,
  OrderSuppliersInputModel,
  OrderSuppliersViewModel,
  WorkflowOrderStepStatus,
} from '@ail/orders-api'

interface SelectAndEditModalProps {
  data?: {
    order: OrderInternalViewModel | OrderSuppliersViewModel
    field: OrderInternalFormFieldViewModel | OrderSuppliersFormFieldViewModel
  }
  onClose: () => void
}

export function SelectAndEditModal(props: SelectAndEditModalProps) {
  const [searchString, setSearchString] = useState('')
  const [showOptions, setShowOptions] = useState(false)
  const [value, setValue] = useState('')
  const [t, i18n] = useTranslation()

  const companyId = props.data?.order.companyId
  const loadManagers = !!companyId && props.data?.field.externalDataSourceName === 'contactFullName'
  const loadCustomers = !!companyId && props.data?.field.externalDataSourceName === 'companyAddresses'
  const customerManagers = useGetCustomerManagersQuery(companyId!, { skip: !loadManagers })
  const allCustomers = useGetAllCustomersQuery(undefined, { skip: !loadCustomers })
  const [updateOrderInternal] = useUpdateOrderInternalMutation({ fixedCacheKey: 'UpdateInternal' })
  const [updateOrderSuppliers] = useUpdateOrderSuppliersMutation({ fixedCacheKey: 'UpdateSuppliers' })
  const title = props.data?.field?.tr?.[i18n.language] ?? props.data?.field?.tr?.['en'] ?? ''

  const items = useMemo(() => {
    if (loadCustomers) {
      const customer = allCustomers.data?.find((c) => c.customer?.id === companyId)
      return (
        customer?.customer?.primary?.addresses.map((a) => {
          return {
            text: `${a.street} ${a.postalCode} ${a.city} ${a.country}`,
            value: `${a.street} ${a.postalCode} ${a.city} ${a.country}`,
          }
        }) ?? []
      )
    } else if (loadManagers) {
      return (
        customerManagers.data?.map((m) => {
          const email = m.contacts.find((c) => c.kind === ContactKind.Email)
          const phone = m.contacts.find((c) => c.kind === ContactKind.PhoneNumber)
          return {
            text: `${m.displayName} ${email?.value} ${phone?.value}`,
            value: `${m.displayName} ${email?.value} ${phone?.value}`,
          }
        }) ?? []
      )
    }
    return []
  }, [loadCustomers, loadManagers, allCustomers.data, companyId, customerManagers.data])

  const handleValueChange = (value: string) => {
    setShowOptions(false)
    setSearchString('')
    setValue(value)
  }

  const update = () => {
    if (!props.data) {
      return
    }
    const { order, field } = props.data
    const model: OrderInternalInputModel | OrderSuppliersInputModel = {
      form: {
        ...order.form,
        fields: [{ name: field.name, value, documents: [] }],
      },
    }
    if (isSupplierOrder(order)) {
      if (model.form.status === WorkflowOrderStepStatus.New) {
        model.form.status = WorkflowOrderStepStatus.InProgress
      }
      updateOrderSuppliers({ id: order.id, model })
    } else {
      updateOrderInternal({ id: order.id, model })
    }
    props.onClose()
  }

  useEffect(() => {
    setValue(props.data?.field.value ?? '')
  }, [props.data])

  const filteredItems = items.filter((i) => !searchString || i.text.toLowerCase().includes(searchString.toLowerCase()))

  return (
    <Dialog open={!!props.data} title={title} onClose={props.onClose} icon="icon-edit" width={530}>
      <div className="select-edit-modal">
        <label className="addressLabel">Search and select the address</label>
        <div className="selectArea">
          <input
            name={`fieldnameveryunique${new Date().getTime()}`}
            type="text"
            autoComplete="none"
            list="autocompleteOff"
            className="required-highlight transparent"
            onChange={(e) => setSearchString(e.target.value)}
            onClick={() => setShowOptions(true)}
            value={searchString}
            placeholder="Please select or write an address"
          />
          <div className="cantonsOptionArea">
            {showOptions &&
              !!filteredItems.length &&
              filteredItems.map((item, idx) => (
                <a key={idx} className="cantonsOption" onClick={() => handleValueChange(item.value)}>
                  {item.value}
                  <i className="icon-add" />
                </a>
              ))}
            {showOptions && !filteredItems.length && (
              <a className="cantonsOption">
                <div>{t('NO_RESULTS_FOUND')}</div>
              </a>
            )}
          </div>
          {!!searchString && <i className="icon-close" onClick={() => setSearchString('')}></i>}
          {!searchString && <i className="icon-bottom" onClick={() => setShowOptions(!showOptions)}></i>}
        </div>
        <label className="addressLabel">Address</label>
        <div className="selectArea">
          <input
            type="text"
            autoComplete="none"
            list="autocompleteOff"
            className="textArea"
            value={value}
            onChange={(e) => handleValueChange(e.currentTarget.value)}
          ></input>
          {!!value && <i className="icon-clear" onClick={() => handleValueChange('')}></i>}
        </div>
        <div className="select-edit-modal-actions">
          <TransparentButton type={ButtonType.Negative} icon="icon-close" onClick={props.onClose} name={t('CANCEL')} />
          <TransparentButton type={ButtonType.Positive} icon="icon-check" onClick={update} name={t('UPDATE')} />
        </div>
      </div>
    </Dialog>
  )
}
